/**
 * useRecaptcha Hook
 * Google reCAPTCHA Enterprise integration for form protection
 */

import { useCallback, useState } from 'react';

// reCAPTCHA Enterprise Site Key
const RECAPTCHA_SITE_KEY = '6Lc2ZiYsAAAAAGifWt_xp1AA197_W8vMpJTOKzz2';

// Action types for different forms
export type RecaptchaAction = 
  | 'LOGIN'
  | 'REGISTER'
  | 'CHECKOUT'
  | 'CONTACT'
  | 'LEAD_FORM'
  | 'PASSWORD_RESET'
  | 'ACCOUNT_UPDATE';

// Extend window type for grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

interface UseRecaptchaReturn {
  executeRecaptcha: (action: RecaptchaAction) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for executing reCAPTCHA Enterprise verification
 */
export function useRecaptcha(): UseRecaptchaReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeRecaptcha = useCallback(async (action: RecaptchaAction): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if grecaptcha is loaded
      if (!window.grecaptcha?.enterprise) {
        throw new Error('reCAPTCHA not loaded. Please refresh the page.');
      }

      // Wait for reCAPTCHA to be ready
      return new Promise((resolve, reject) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(
              RECAPTCHA_SITE_KEY,
              { action }
            );
            setIsLoading(false);
            resolve(token);
          } catch (err) {
            setIsLoading(false);
            const errorMessage = err instanceof Error ? err.message : 'reCAPTCHA execution failed';
            setError(errorMessage);
            reject(err);
          }
        });
      });
    } catch (err) {
      setIsLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'reCAPTCHA initialization failed';
      setError(errorMessage);
      return null;
    }
  }, []);

  return {
    executeRecaptcha,
    isLoading,
    error,
  };
}

/**
 * Utility function for one-off reCAPTCHA execution (non-hook context)
 */
export async function getRecaptchaToken(action: RecaptchaAction): Promise<string> {
  if (!window.grecaptcha?.enterprise) {
    throw new Error('reCAPTCHA not loaded');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(
          RECAPTCHA_SITE_KEY,
          { action }
        );
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });
}

export { RECAPTCHA_SITE_KEY };
